import * as plugins from '../../00plugins.js';
import { zIndexRegistry } from '../../00zindex.js';
import { runAction } from '../../00actionerror.js';

import { demoFunc } from './dees-modal.demo.js';
import {
  customElement,
  html,
  DeesElement,
  property,
  type TemplateResult,
  cssManager,
  css,
  state,
} from '@design.estate/dees-element';

import '../../00group-button/dees-button/dees-button.js';
import '../../00group-button/dees-button-exit/dees-button-exit.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import { themeDefaultStyles } from '../../00theme.js';
import { DeesInputText } from '../../00group-input/dees-input-text/dees-input-text.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-modal': DeesModal;
  }
}

@customElement('dees-modal')
export class DeesModal extends DeesElement {
  // STATIC
  public static demo = demoFunc;
  public static demoGroups = ['Overlay'];

  /** A single-value dialog. Confirm failures stay visible; every dismissal resolves null. */
  public static async prompt(options: {
    heading: string;
    label: string;
    /** What confirming does, shown above the field and announced as the dialog's description. */
    description?: string | TemplateResult;
    value?: string;
    confirmLabel?: string;
    /** Shown on the field when it is empty on confirmation. */
    emptyValueMessage?: string;
    validate?: (value: string) => string | undefined;
    onConfirm?: (value: string) => void | Promise<void>;
  }): Promise<string | null> {
    const input = new DeesInputText();
    input.label = options.label;
    input.value = options.value ?? '';
    input.required = true;
    let resolve!: (value: string | null) => void;
    const result = new Promise<string | null>(resolveResult => { resolve = resolveResult; });
    let modal: DeesModal;
    const confirm = async () => {
      if (modal.promptPending) return;
      const value = String(input.value ?? '').trim();
      const error = value ? options.validate?.(value) : options.emptyValueMessage ?? 'Enter a name.';
      if (error) {
        input.validationState = 'invalid'; input.validationText = error;
        await input.focus();
        return;
      }
      modal.promptPending = true;
      input.disabled = true;
      try {
        await options.onConfirm?.(value);
        modal.promptPending = false;
        resolve(value);
        await modal.destroy();
      } catch (error) {
        modal.promptPending = false;
        input.disabled = false;
        input.validationState = 'invalid';
        input.validationText = error instanceof Error ? error.message : String(error);
        await input.updateComplete;
        await input.focus();
      }
    };
    input.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.isComposing || event.repeat) return;
      event.preventDefault(); event.stopPropagation();
      void confirm();
    });
    modal = await DeesModal.createAndShow({
      heading: options.heading, width: 'small', description: options.description, content: html`${input}`,
      menuOptions: [
        { name: 'Cancel', action: async () => { await modal.destroy(); } },
        { name: options.confirmLabel ?? 'Create', action: confirm },
      ],
    });
    modal.addEventListener('modal-close', () => resolve(null), { once: true });
    await input.updateComplete;
    await input.focus();
    return result;
  }

  public static async createAndShow(optionsArg: {
    heading: string;
    subheading?: string;
    /** Body copy above the content, announced as the dialog's description. */
    description?: string | TemplateResult;
    sidebar?: TemplateResult;
    content: TemplateResult;
    menuOptions: plugins.tsclass.website.IMenuItem<DeesModal>[];
    width?: 'small' | 'medium' | 'large' | 'fullscreen' | number;
    /** Fixed height in both layouts, clamped to the viewport. Sidebar defaults: 400px desktop, 520px at <=600px. */
    height?: number;
    maxWidth?: number;
    minWidth?: number;
    showCloseButton?: boolean;
    showHelpButton?: boolean;
    onHelp?: () => void | Promise<void>;
    mobileFullscreen?: boolean;
    contentPadding?: number;
  }) {
    const body = document.body;
    const modal = new DeesModal();
    modal.heading = optionsArg.heading;
    modal.subheading = optionsArg.subheading ?? '';
    modal.description = optionsArg.description;
    modal.sidebar = optionsArg.sidebar;
    modal.content = optionsArg.content;
    modal.menuOptions = optionsArg.menuOptions;
    if (optionsArg.width) modal.width = optionsArg.width;
    if (optionsArg.height !== undefined) modal.height = optionsArg.height;
    if (optionsArg.maxWidth) modal.maxWidth = optionsArg.maxWidth;
    if (optionsArg.minWidth) modal.minWidth = optionsArg.minWidth;
    if (optionsArg.showCloseButton !== undefined) modal.showCloseButton = optionsArg.showCloseButton;
    if (optionsArg.showHelpButton !== undefined) modal.showHelpButton = optionsArg.showHelpButton;
    if (optionsArg.onHelp) modal.onHelp = optionsArg.onHelp;
    if (optionsArg.mobileFullscreen !== undefined) modal.mobileFullscreen = optionsArg.mobileFullscreen;
    if (optionsArg.contentPadding !== undefined) modal.contentPadding = optionsArg.contentPadding;
    body.append(modal);
    await modal.updateComplete;
    
    // Get z-index for modal (should be above window layer)
    modal.modalZIndex = zIndexRegistry.getNextZIndex();
    zIndexRegistry.register(modal, modal.modalZIndex);
    
    return modal;
  }

  // INSTANCE

  @property({ type: String })
  accessor subheading = '';

  /** Body copy above the content. Set, it also describes the dialog for assistive technology. */
  @property({ attribute: false })
  accessor description: string | TemplateResult | undefined;

  @property({ attribute: false })
  accessor sidebar: TemplateResult | undefined;

  @property({
    type: String,
  })
  accessor heading = '';

  @state({})
  accessor content!: TemplateResult;

  @state({})
  accessor menuOptions: plugins.tsclass.website.IMenuItem<DeesModal>[] = [];

  @property({ type: String })
  accessor width: 'small' | 'medium' | 'large' | 'fullscreen' | number = 'medium';

  @property({ type: Number })
  accessor height: number | undefined;

  @property({ type: Number })
  accessor maxWidth!: number;

  @property({ type: Number })
  accessor minWidth!: number;

  @property({ type: Boolean })
  accessor showCloseButton: boolean = true;

  @property({ type: Boolean })
  accessor showHelpButton: boolean = false;

  @property({ attribute: false })
  accessor onHelp!: () => void | Promise<void>;

  @property({ type: Boolean })
  accessor mobileFullscreen: boolean = false;

  @property({ type: Number })
  accessor contentPadding: number = 24;

  @state()
  accessor modalZIndex: number = 1000;

  @state() private accessor promptPending = false;

  /** The menu action whose promise is still pending, if any. */
  @state() private accessor pendingAction: plugins.tsclass.website.IMenuItem<DeesModal> | null = null;

  constructor() {
    super();
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host { font-family: var(--dees-font-family); color: var(--dees-color-text-primary); }
      .modalContainer {
        position: fixed;
        inset: 0;
        margin: 0;
        padding: 24px;
        border: none;
        width: 100vw;
        height: 100dvh;
        max-width: none;
        max-height: none;
        box-sizing: border-box;
        background: transparent;
        color: inherit;
        overflow: hidden;
      }
      .modalContainer[open] { display: flex; align-items: center; justify-content: center; }
      .modalContainer::backdrop {
        background: var(--dees-color-scrim);
      }
      .sheet {
        display: grid;
        grid-template-rows: auto minmax(0, 1fr) auto;
        grid-template-columns: minmax(0, 1fr);
        background: var(--dees-material-thick-bg-opaque);
        border: none;
        border-radius: var(--dees-radius-2xl);
        corner-shape: var(--dees-corner-shape);
        box-shadow: 0 0 0 1px var(--dees-color-border-subtle), var(--dees-shadow-xl), inset 0 1px 0 var(--dees-color-surface-highlight);
        overflow: hidden;
        box-sizing: border-box;
        min-height: min(120px, 100%);
        min-width: min(var(--modal-min-width, 0px), 100%);
        max-width: min(var(--modal-max-width, 100%), 100%);
        max-height: 100%;
        height: var(--modal-height, auto);
        width: var(--modal-width, 560px);
        animation: enter var(--dees-transition-default) var(--dees-ease-out);
      }
      .sheet.has-sidebar { grid-template-columns: 200px minmax(0, 1fr); height: var(--modal-height, var(--modal-sidebar-height, 400px)); }
      .sidebar { grid-column: 1; grid-row: 1 / 4; padding: 20px 12px; background: var(--dees-color-modal-sidebar); border-right: 1px solid var(--dees-color-border-subtle); overflow-y: auto; }
      .has-sidebar > .heading, .has-sidebar > .content, .has-sidebar > .bottomButtons { grid-column: 2; }
      .heading { grid-row: 1; }
      .content { grid-row: 2; overflow-y: auto; overscroll-behavior: contain; scrollbar-width: thin; }
      .bottomButtons { grid-row: 3; border-top: 1px solid var(--dees-color-border-subtle); background: var(--dees-color-modal-sidebar); }
      .heading-copy { flex: 1; min-width: 0; }
      .subheading { margin: 6px 0 0; font-size: 13px; line-height: 1.5; color: var(--dees-color-text-secondary); }
      .description { margin: 0 0 var(--dees-spacing-lg); font-size: 13px; line-height: 1.5; color: var(--dees-color-text-secondary); }
      .content > dees-form { padding: 16px; background: var(--dees-color-settings-group); border: none; border-radius: var(--dees-radius-xl); }
      .content > dees-form[grouped] { padding: 0; }
      .sheet.width-small { width: 380px; }
      .sheet.width-medium { width: 560px; }
      .sheet.width-large { width: 800px; }
      .sheet.width-fullscreen { width: 100%; height: 100%; }
      .heading {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-md);
        padding: 20px 24px 0;
      }
      .heading-text {
        flex: 1;
        min-width: 0;
        margin: 0;
        font-size: 17px;
        line-height: 1.3;
        font-weight: 600;
        letter-spacing: -0.01em;
        overflow-wrap: anywhere;
        outline: none;
      }
      .header-buttons { display: flex; gap: var(--dees-spacing-sm); align-items: center; align-self: flex-start; margin-top: -3px; }
      .help-button {
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-full);
        width: 28px;
        height: 28px;
        background: transparent;
        color: var(--dees-color-text-secondary);
        cursor: pointer;
      }
      .help-button:hover { background: var(--dees-color-hover); }
      .help-button:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: 2px; }
      .content { min-width: 0; overflow-wrap: anywhere; }
      .bottomButtons {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        align-items: center;
        gap: var(--dees-spacing-sm);
        padding: var(--dees-spacing-md) var(--dees-spacing-xl);
      }
      .modal-action::part(button) { height: 28px; font-size: 13px; border-radius: 8px; }
      .predestroy { animation: leave var(--dees-transition-fast) var(--dees-ease-in) forwards; }
      @keyframes enter { from { opacity: 0; transform: translateY(8px) scale(.98); } }
      @keyframes leave { to { opacity: 0; transform: translateY(4px) scale(.99); } }
      @media (max-width: 600px) {
        .modalContainer { padding: 12px; }
        .modalContainer:has(.mobile-fullscreen) { padding: 0; }
        .sheet.mobile-fullscreen { width: 100%; height: 100%; }
        .sheet.mobile-fullscreen { border-radius: 0; border: none; }
        .sheet.has-sidebar { grid-template-columns: minmax(0, 1fr); min-height: 0; --modal-sidebar-height: 520px; }
        .sidebar { grid-column: 1; grid-row: auto; border-right: none; border-bottom: 1px solid var(--dees-color-border-subtle); padding: 12px; }
        .has-sidebar > .heading, .has-sidebar > .content, .has-sidebar > .bottomButtons { grid-column: 1; grid-row: auto; }
        .has-sidebar { grid-template-rows: auto auto minmax(0,1fr) auto; }
        .heading, .bottomButtons { padding-left: 16px; padding-right: 16px; }
      }
      @media (prefers-reduced-motion: reduce) { .sheet, .predestroy { animation: none; } }
      @media (prefers-reduced-transparency: reduce) { .modalContainer::backdrop { backdrop-filter: none; } }
    `,
  ];

  public render(): TemplateResult {
    const widthClass = typeof this.width === 'string' ? `width-${this.width}` : '';
    const sizing = [
      typeof this.width === 'number' ? `--modal-width:${this.width}px` : '',
      this.height !== undefined ? `--modal-height:${this.height}px` : '',
      this.maxWidth ? `--modal-max-width:${this.maxWidth}px` : '',
      this.minWidth ? `--modal-min-width:${this.minWidth}px` : '',
    ].join(';');
    return html`
      <dialog class="modalContainer" aria-labelledby="modal-heading" aria-modal="true"
        @cancel=${this.handleCancel} @click=${this.handleOutsideClick}>
        <section class="sheet ${widthClass} ${this.mobileFullscreen ? 'mobile-fullscreen' : ''} ${this.sidebar ? 'has-sidebar' : ''}"
          style=${sizing}>
          ${this.sidebar ? html`<aside class="sidebar">${this.sidebar}</aside>` : ''}
          <div class="heading">
            <div class="heading-copy"><h2 id="modal-heading" class="heading-text" tabindex="-1" autofocus>${this.heading}</h2>
              ${this.subheading ? html`<p class="subheading">${this.subheading}</p>` : ''}</div>
            <div class="header-buttons">
              ${this.showHelpButton ? html`<button class="help-button" type="button" aria-label="Help" @click=${this.handleHelp}>?</button>` : ''}
              ${this.showCloseButton ? html`<dees-button-exit .size=${28} @click=${() => this.destroy()}></dees-button-exit>` : ''}
            </div>
          </div>
          <div class="content" style="padding:${this.contentPadding}px">${this.description
            ? html`<p id="modal-description" class="description">${this.description}</p>` : ''}${this.content}</div>
          ${this.menuOptions.length ? html`
            <div class="bottomButtons">
              ${this.menuOptions.map((action, index) => html`
                <dees-button class="modal-action" .text=${action.name}
                  .disabled=${this.promptPending || (this.pendingAction !== null && this.pendingAction !== action)}
                  .status=${this.pendingAction === action ? 'pending' : 'normal'}
                  .type=${index === this.menuOptions.length - 1 ? 'accent' : 'secondary'}
                  @clicked=${() => this.runMenuAction(action)}></dees-button>
              `)}
            </div>
          ` : ''}
        </section>
      </dialog>
    `;
  }

  private destroyPromise?: Promise<void>;

  public firstUpdated(changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(changedProperties);
    if (this.isConnected) this.dialog?.showModal();
  }

  /**
   * The description is announced with the dialog. An IDREF binding cannot be
   * removed through a template attribute, and neither a dangling reference nor
   * an empty one is a valid description, so the relation is set on the element:
   * a dialog without a description carries no `aria-describedby` at all.
   */
  public updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    const dialog = this.dialog;
    if (!dialog) return;
    if (this.description) dialog.setAttribute('aria-describedby', 'modal-description');
    else dialog.removeAttribute('aria-describedby');
  }

  private get dialog(): HTMLDialogElement | null {
    return this.shadowRoot?.querySelector('dialog') ?? null;
  }

  private handleCancel(eventArg: Event): void {
    eventArg.preventDefault();
    void this.destroy();
  }

  public async handleOutsideClick(eventArg: MouseEvent) {
    if (eventArg.composedPath()[0] === this.dialog) await this.destroy();
  }

  public destroy(): Promise<void> {
    if (this.promptPending) return Promise.resolve();
    return this.destroyPromise ??= this.closeAndRemove();
  }

  private async closeAndRemove(): Promise<void> {
    const tile = this.shadowRoot?.querySelector('.sheet');
    tile?.classList.add('predestroy');
    // Wait for the actual CSS animation; reduced motion has no animation to wait for.
    if (tile) await Promise.allSettled(tile.getAnimations().map((animation) => animation.finished));
    this.dialog?.close();
    this.remove();
  }

  public async disconnectedCallback(): Promise<void> {
    this.dialog?.close();
    zIndexRegistry.unregister(this);
    this.dispatchEvent(new CustomEvent('modal-close'));
    await super.disconnectedCallback();
  }

  private async handleHelp() {
    if (this.onHelp) await this.onHelp();
  }

  /**
   * A menu action is awaited so a rejected callback surfaces as a visible
   * failure instead of an unhandled rejection. The error is announced on the
   * modal through the cancelable `dees-action-error` event and, unless a
   * listener cancels it, shown as a notification inside this dialog.
   *
   * One action runs at a time: while its promise is pending, its own button
   * shows the busy face and the other actions are disabled, so a second press
   * cannot run an irreversible operation twice. Both a resolution and a
   * rejection release the dialog — a failed action keeps it open and usable.
   * Dismissal is deliberately not blocked; an action that closes the dialog
   * itself does so through `destroy()` while it is still the pending action.
   */
  private async runMenuAction(actionArg: plugins.tsclass.website.IMenuItem<DeesModal>) {
    if (this.pendingAction) return;
    this.pendingAction = actionArg;
    try {
      await runAction(this, { action: actionArg, source: 'modal-menu', ownerElement: this.dialog }, () => actionArg.action(this));
    } finally {
      this.pendingAction = null;
    }
  }
}
